<template>
    <QuestionBox :title="step.question" :text="$t('module.orderItemsText')" />
    <div class="m-orderList" ref="El">
        <VueDraggable
            class="m-orderList__inner"
            ref="orderEl"
            v-model="itemsShuffled"
            :animation="150"
            @Sort="onDragChange"
        >
            <div
                class="m-orderList__item u-borderTertiary u-mb10"
                :class="{
                    'u-borderGreen -correct': matchedIndexes.matchingIndexes?.includes(index),
                    'u-borderRed -incorrect': matchedIndexes.nonMatchingIndexes?.includes(index),
                }"
                v-for="(item, index) in itemsShuffled"
                :key="item.id"
            >
                <div class="m-orderList__itemDragHandle">
                    <svg class="m-orderList__itemDragIcon" width="10" height="15" viewBox="0 0 10 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="1.92857" cy="1.92857" r="1.92857"/>
                        <circle cx="1.92857" cy="7.5001" r="1.92857"/>
                        <circle cx="1.92857" cy="13.0714" r="1.92857"/>
                        <circle cx="7.49986" cy="1.92857" r="1.92857"/>
                        <circle cx="7.49986" cy="7.5001" r="1.92857"/>
                        <circle cx="7.49986" cy="13.0714" r="1.92857"/>
                    </svg>
                </div>
                <div
                    class="m-orderList__itemNumber a-circle -small -tertiary"
                >
                    {{ index + 1 }}
                </div>
                <div class="m-orderList__itemText">{{ item.item }}</div>
            </div>
        </VueDraggable>
    </div>
    <FixedButton
        :isDisabled="!hasBeenDragged"
        @click="handleButtonClick"
        :buttonClass="showTryAgain ? '-reset' : ''"
    >
        {{ showTryAgain ? $t('module.tryAgain') : (answeredCorrect ? $t('module.nextQuestion') : $t('module.checkQuestion')) }}
    </FixedButton>
</template>

<script setup>
import { ref, inject } from 'vue';
import { VueDraggable } from 'vue-draggable-plus';
import { useI18n } from 'vue-i18n';
import { shuffleArray, addAttempt } from '../../utils/globalFunctions';
import 'drag-drop-touch';

// Components
import QuestionBox from './QuestionBox.vue';
import FixedButton from './FixedButton.vue';

const props = defineProps({
    step: Object,
});

const { t } = useI18n();
const itemsShuffled = ref(shuffleArray([...props.step.items]));
const hasBeenDragged = ref(false);
const El = ref(null);
const showTryAgain = ref(false);
const answeredCorrect = ref(false);
const matchedIndexes = ref({ matchingIndexes: [], nonMatchingIndexes: [] });

const completeStep = inject('completeStep');
const showNotice = inject('showNotice');
const hideNotice = inject('hideNotice');

const attempts = defineModel( 'attempts' );

const isOrderMatching = (originalArray, currentArray) => {
    const matchingIndexes = [];
    const nonMatchingIndexes = [];

    originalArray.forEach((item, index) => {
        if (item.item === currentArray[index].item) {
            matchingIndexes.push(index);
        } else {
            nonMatchingIndexes.push(index);
        }
    });

    return { matchingIndexes, nonMatchingIndexes };
};

const checkAnswer = () => {
    El.value.classList.add('-disableDrag');

    matchedIndexes.value = isOrderMatching(props.step.items, itemsShuffled.value);
    if (matchedIndexes.value.nonMatchingIndexes.length === 0) {

        attempts.value.push( addAttempt( props.step.uuid, true ) );

        answeredCorrect.value = true;
        const textCorrect = props.step.feedback_correct ? props.step.feedback_correct : t('module.correctOrderText');
        showNotice(t('module.correctOrderTitle'), textCorrect, '-success');
        completeStep(props.step.uuid);
    } else {

        attempts.value.push( addAttempt( props.step.uuid, false ) );

        showNotice(t('module.wrongOrderTitle'), t('module.wrongOrderText'), '-error');
        showTryAgain.value = true; 
    }
};

const resetQuestion = () => {
    El.value.classList.remove('-disableDrag');

    itemsShuffled.value = shuffleArray([...props.step.items]);

    matchedIndexes.value = { matchingIndexes: [], nonMatchingIndexes: [] };
    showTryAgain.value = false;
    hideNotice();
    hasBeenDragged.value = false;
};

const handleButtonClick = () => {
    if (showTryAgain.value) {
        resetQuestion();
    } else if (answeredCorrect.value) {
        completeStep(props.step.uuid, true, false);
        hideNotice();
    } else {
        checkAnswer();
    }
};

const onDragChange = () => {
    hasBeenDragged.value = true;
};
</script>